import {
  type AppSettings,
  SYNC_TRIGGER_LABELS,
  type SyncSummary,
  TIME_OF_DAY_PATTERN,
  type UpdateAppSettings,
} from '@acasa/shared'
import { useEffect, useId, useState } from 'react'
import { Card } from '@/components/card'
import { useSession } from '@/features/auth/use-auth'
import { useSyncStatus } from '@/features/checklists/use-checklists'
import { TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'
import { relativeTime } from '@/lib/relative-time'
import { useAppSettings, useUpdateAppSettings } from './use-app-settings'

/**
 * The rules about the checklist mirror: which onOffice notes close a checklist
 * step, whether the sync mirrors archived objects, and when it runs by itself.
 *
 * Admin only, here and — the half that counts — in the API, which answers 403.
 *
 * Edits go into a local draft and are sent on "Speichern", not on every
 * keystroke. Saving the note list re-evaluates the whole mirror server-side, so
 * an autosave here would mean re-running that on the way to typing a word.
 */

/*
 * Mirrored from `notePrefixSchema` in @acasa/shared. The server is still the
 * one that enforces them; repeating them here is only so a mistake reads as a
 * German sentence in the card instead of arriving as a 400.
 */
const MIN_PREFIX_LENGTH = 2
const MAX_PREFIX_LENGTH = 60
const MAX_PREFIXES = 20

/**
 * The groups THIS card edits. The settings have more — the Makler block
 * has a card of its own — and a draft typed as the whole object would have this
 * card's "Standard wiederherstellen" and its dirty check reach into settings
 * it does not show.
 */
type CardSettings = Pick<AppSettings, 'checklist' | 'estates' | 'sync'>

export function AppSettingsCard() {
  const { user } = useSession()
  const isAdmin = user?.role === 'admin'

  const query = useAppSettings(isAdmin)
  const save = useUpdateAppSettings()
  const status = useSyncStatus(isAdmin)

  const server = query.data?.settings ?? null
  const defaults = query.data?.defaults ?? null

  const [draft, setDraft] = useState<CardSettings | null>(null)
  const [entry, setEntry] = useState('')
  const [entryError, setEntryError] = useState<string | null>(null)
  const [saved, setSaved] = useState<{ items: number; archivedChanged: boolean } | null>(null)

  const entryFieldId = useId()
  const timeFieldId = useId()

  /*
   * React Query keeps `settings` referentially stable across a refetch that
   * returns the same values (structural sharing), so this fires when the stored
   * settings actually CHANGE — a background refetch cannot throw away
   * half-finished edits. After a save it lands the draft exactly on what the
   * server stored, including anything it normalised on the way in.
   *
   * Keyed on this card's GROUPS, not on the settings object: the same
   * sharing keeps those stable when another card saves a different group, and
   * a Makler save must not wipe a half-edited note list here.
   */
  const checklist = server?.checklist
  const estates = server?.estates
  const sync = server?.sync
  useEffect(() => {
    setDraft(checklist && estates && sync ? clone({ checklist, estates, sync }) : null)
    setEntry('')
    setEntryError(null)
  }, [checklist, estates, sync])

  if (!isAdmin) return null

  const notes = draft?.checklist.notApplicableNotes ?? []
  const dirty = draft !== null && server !== null && !sameSettings(draft, server)
  const atDefaults = draft !== null && defaults !== null && sameSettings(draft, defaults)
  const busy = save.isPending
  /*
   * `<input type="time">` normally hands back "HH:MM", but desktop Safari
   * renders a plain text field, where "6 Uhr" is as easy to type as "06:00".
   * The server would refuse it with a 400; here it just keeps Speichern off.
   */
  const timeInvalid = draft !== null && !TIME_OF_DAY_PATTERN.test(draft.sync.dailyAt)

  /** Any edit invalidates the last confirmation — it described a state that is gone. */
  const edit = (next: CardSettings) => {
    setDraft(next)
    setSaved(null)
  }

  const addNote = () => {
    if (!draft) return

    // Lowercased on the way in, which is how they are stored — so a plain
    // `includes` already rejects "Nicht zutreffend" next to "nicht zutreffend".
    const value = entry.trim().toLowerCase()

    const problem =
      value.length < MIN_PREFIX_LENGTH
        ? 'Mindestens zwei Zeichen — ein einzelner Buchstabe würde fast jede Notiz treffen.'
        : value.length > MAX_PREFIX_LENGTH
          ? `Höchstens ${MAX_PREFIX_LENGTH} Zeichen.`
          : notes.includes(value)
            ? `„${value}“ steht bereits in der Liste.`
            : notes.length >= MAX_PREFIXES
              ? `Höchstens ${MAX_PREFIXES} Einträge.`
              : null

    if (problem) {
      setEntryError(problem)
      return
    }

    edit({ ...draft, checklist: { notApplicableNotes: [...notes, value] } })
    setEntry('')
    setEntryError(null)
  }

  const submit = () => {
    if (!draft || !server) return

    // Only the group(s) that differ. A group that is sent is sent whole, so
    // sending both would have the server re-evaluate the mirror for a change
    // that was really only about archived objects.
    const patch: UpdateAppSettings = {}
    if (!sameNotes(notes, server.checklist.notApplicableNotes)) {
      patch.checklist = { notApplicableNotes: notes }
    }
    const archivedChanged = draft.estates.includeArchived !== server.estates.includeArchived
    if (archivedChanged) patch.estates = { includeArchived: draft.estates.includeArchived }
    if (!sameSchedule(draft.sync, server.sync)) patch.sync = { ...draft.sync }

    setSaved(null)
    save.mutate(patch, {
      onSuccess: (response) => setSaved({ items: response.reevaluatedItems, archivedChanged }),
    })
  }

  return (
    <Card
      title="Unterlagen und Abgleich"
      footer={
        query.data?.updatedAt
          ? `Gilt für alle Konten. Zuletzt gespeichert am ${formatMoment(query.data.updatedAt)}.`
          : 'Gilt für alle Konten. Bisher unverändert — es gelten die Standardwerte.'
      }
    >
      {query.isPending && <p className="text-muted text-sm">Wird geladen …</p>}

      {query.isError && (
        <p className="text-danger text-sm">
          Einstellungen konnten nicht geladen werden: {errorMessage(query.error)}
        </p>
      )}

      {draft && (
        <div className="flex flex-col gap-5">
          <section>
            <h3 className="text-ink text-sm font-medium">Notizen, die ein Dokument schließen</h3>
            <p className="text-muted mt-1 text-sm leading-relaxed">
              Beginnt die onOffice-Notiz zu einem offenen Dokument mit einem dieser Textanfänge,
              zählt das Dokument als „nicht zutreffend“ statt als offen. Groß- und Kleinschreibung
              spielt keine Rolle, und was danach folgt ebenfalls nicht — der Eintrag{' '}
              <span className="text-ink font-mono text-xs">nicht zutreffend</span> erfasst also auch
              die Notiz „Nicht zutreffend!“.
            </p>

            {notes.length > 0 ? (
              <ul className="mt-3 flex flex-wrap gap-2">
                {notes.map((note) => (
                  <li
                    key={note}
                    className="border-line bg-raised flex max-w-full items-center gap-2 border px-2 py-1"
                  >
                    <span className="text-ink min-w-0 truncate font-mono text-xs">{note}</span>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`„${note}“ entfernen`}
                      onClick={() =>
                        edit({
                          ...draft,
                          checklist: {
                            notApplicableNotes: notes.filter((value) => value !== note),
                          },
                        })
                      }
                      className="text-faint hover:text-danger shrink-0 text-sm leading-none transition-colors disabled:opacity-40"
                    >
                      <span aria-hidden>×</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-faint mt-3 text-xs">
                Die Liste ist leer — dann schließt keine Notiz ein Dokument, jedes offene Dokument
                bleibt offen.
              </p>
            )}

            <form
              className="mt-3"
              onSubmit={(event) => {
                event.preventDefault()
                addNote()
              }}
            >
              <label
                htmlFor={entryFieldId}
                className="text-muted mb-1 block text-[11px] tracking-wide uppercase"
              >
                Textanfang hinzufügen
              </label>
              <div className="flex flex-wrap items-start gap-2">
                <TextInput
                  id={entryFieldId}
                  className="min-w-0 flex-1"
                  placeholder="z. B. nicht zutreffend"
                  value={entry}
                  disabled={busy || notes.length >= MAX_PREFIXES}
                  onChange={(event) => {
                    setEntry(event.target.value)
                    setEntryError(null)
                  }}
                />
                <button
                  type="submit"
                  disabled={busy || entry.trim().length === 0 || notes.length >= MAX_PREFIXES}
                  className="border-line hover:border-line-strong text-muted hover:text-ink shrink-0 border px-3 py-2.5 text-xs transition-colors disabled:opacity-40"
                >
                  Hinzufügen
                </button>
              </div>
              {entryError && <p className="text-danger mt-2 text-xs">{entryError}</p>}
            </form>
          </section>

          <section className="border-line border-t pt-5">
            <h3 className="text-ink text-sm font-medium">Archivierte Objekte</h3>

            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                disabled={busy}
                checked={draft.estates.includeArchived}
                onChange={(event) =>
                  edit({ ...draft, estates: { includeArchived: event.target.checked } })
                }
                className="accent-brand mt-0.5 size-4 shrink-0"
              />
              <span className="min-w-0">
                <span className="text-ink block text-sm">Beim Abgleich mit übernehmen</span>
                <span className="text-faint mt-1 block text-[11px] leading-relaxed">
                  Wirkt sich erst beim nächsten Abgleich aus — die Einstellung wird gelesen, wenn
                  ein Abgleich startet, nicht wenn sie gespeichert wird.
                </span>
                <span className="text-faint mt-1 block text-[11px] leading-relaxed">
                  Eingeschaltet werden archivierte Objekte mit abgeglichen — der Abgleich dauert
                  dadurch länger. Ob sie in der Unterlagen-Liste erscheinen, entscheiden weiterhin
                  die Filter dort. Ausgeschaltet werden sie nur dann live aus onOffice geholt, wenn
                  ein Übergabeprotokoll eines braucht.
                </span>
              </span>
            </label>
          </section>

          <section className="border-line border-t pt-5">
            <h3 className="text-ink text-sm font-medium">Automatischer Abgleich</h3>

            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                disabled={busy}
                checked={draft.sync.enabled}
                onChange={(event) =>
                  edit({ ...draft, sync: { ...draft.sync, enabled: event.target.checked } })
                }
                className="accent-brand mt-0.5 size-4 shrink-0"
              />
              <span className="text-ink text-sm">
                Täglich automatisch aus onOffice aktualisieren
              </span>
            </label>

            <div className="mt-3 pl-6">
              <label
                htmlFor={timeFieldId}
                className="text-muted mb-1 block text-[11px] tracking-wide uppercase"
              >
                Uhrzeit
              </label>
              {/* Sized by a wrapper: TextInput is `w-full`, which wins over a width passed in. */}
              <div className="w-32">
                <TextInput
                  id={timeFieldId}
                  type="time"
                  step={60}
                  value={draft.sync.dailyAt}
                  disabled={busy || !draft.sync.enabled}
                  onChange={(event) =>
                    edit({ ...draft, sync: { ...draft.sync, dailyAt: event.target.value } })
                  }
                />
              </div>
              {timeInvalid && (
                <p className="text-danger mt-2 text-xs">Uhrzeit bitte als HH:MM, z. B. 06:00.</p>
              )}
              <p className="text-faint mt-2 text-[11px] leading-relaxed">
                Deutsche Zeit. Ein verpasster Abgleich — der Server war zur eingestellten Zeit nicht
                erreichbar, oder die Einstellung wurde erst später am Tag eingeschaltet — startet
                innerhalb einer Minute. Ein „Aktualisieren“ auf der Unterlagen-Seite zählt als
                Abgleich des Tages.
              </p>
            </div>

            <ScheduleStatus
              nextRunAt={status.data?.nextRunAt ?? null}
              lastRun={status.data?.lastRun ?? null}
              loaded={status.data !== undefined}
            />
          </section>

          <div className="border-line flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-5">
            <button
              type="button"
              disabled={!dirty || busy || timeInvalid}
              onClick={submit}
              className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {busy ? 'wird gespeichert …' : 'Speichern'}
            </button>

            {dirty && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (!server) return
                  setDraft(clone(server))
                  setEntryError(null)
                }}
                className="text-muted hover:text-ink text-xs underline underline-offset-4 transition-colors disabled:opacity-40"
              >
                Verwerfen
              </button>
            )}

            {/* Loads the defaults INTO the draft — it is a starting point to
                look at, not an action that reaches the server on its own. */}
            <button
              type="button"
              disabled={busy || atDefaults || !defaults}
              onClick={() => {
                if (defaults) edit(clone(defaults))
              }}
              className="text-muted hover:text-ink ml-auto text-xs underline underline-offset-4 transition-colors disabled:opacity-40"
            >
              Standard wiederherstellen
            </button>
          </div>

          {save.isError && <p className="text-danger text-xs">{errorMessage(save.error)}</p>}

          {saved && (
            <p className="text-positive text-xs">
              {saved.items > 0
                ? `Gespeichert — ${saved.items} ${
                    saved.items === 1 ? 'Unterlagen-Eintrag wurde' : 'Unterlagen-Einträge wurden'
                  } neu bewertet.`
                : 'Gespeichert.'}
              {saved.archivedChanged && (
                <span className="text-muted mt-1 block">
                  Die Änderung an den archivierten Objekten greift erst beim nächsten Abgleich.
                </span>
              )}
            </p>
          )}
        </div>
      )}
    </Card>
  )
}

/**
 * What the SERVER says about the schedule — the saved one, not the draft. A
 * "Nächster Abgleich" computed from unsaved fields would promise a run nobody
 * has scheduled yet; after a save the status is refetched and this follows.
 */
function ScheduleStatus({
  nextRunAt,
  lastRun,
  loaded,
}: {
  nextRunAt: string | null
  lastRun: SyncSummary | null
  loaded: boolean
}) {
  if (!loaded) return null

  return (
    <div className="mt-4 flex flex-col gap-1 text-xs">
      <p className="text-muted">
        {nextRunAt
          ? `Nächster Abgleich: ${formatMoment(nextRunAt)}`
          : 'Kein automatischer Abgleich geplant.'}
      </p>
      {lastRun && (
        <p className="text-muted">
          Letzter Abgleich: {relativeTime(lastRun.finishedAt ?? lastRun.startedAt)} ·{' '}
          {SYNC_TRIGGER_LABELS[lastRun.trigger]} · {RUN_OUTCOME[lastRun.status]}
        </p>
      )}
      {lastRun?.status === 'failed' && lastRun.error && (
        <p className="border-danger/40 bg-danger/10 text-danger mt-1 border px-3 py-2">
          {lastRun.error}
        </p>
      )}
    </div>
  )
}

const RUN_OUTCOME: Record<SyncSummary['status'], string> = {
  running: 'läuft gerade',
  succeeded: 'erfolgreich',
  failed: 'fehlgeschlagen',
}

/**
 * Order-insensitive: the list is a SET of prefixes, so moving one does not make
 * the form dirty and does not earn a save that re-evaluates the whole mirror.
 */
function sameNotes(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const sorted = [...b].sort()
  return [...a].sort().every((value, index) => value === sorted[index])
}

/**
 * The draft is seeded from objects that live in the query cache. Copying them
 * keeps an edit here from reaching into the cached response — every update
 * below builds new objects, and this is what keeps that a local decision rather
 * than something the next reader has to check.
 */
function clone(settings: CardSettings): CardSettings {
  return {
    checklist: { notApplicableNotes: [...settings.checklist.notApplicableNotes] },
    estates: { ...settings.estates },
    sync: { ...settings.sync },
  }
}

function sameSchedule(a: CardSettings['sync'], b: CardSettings['sync']): boolean {
  return a.enabled === b.enabled && a.dailyAt === b.dailyAt
}

function sameSettings(a: CardSettings, b: CardSettings): boolean {
  return (
    a.estates.includeArchived === b.estates.includeArchived &&
    sameSchedule(a.sync, b.sync) &&
    sameNotes(a.checklist.notApplicableNotes, b.checklist.notApplicableNotes)
  )
}

function formatMoment(iso: string): string {
  const moment = new Date(iso)
  return `${moment.toLocaleDateString('de-DE')} um ${moment.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })} Uhr`
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return 'Unbekannter Fehler'
}
